import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Table } from 'react-bootstrap';
import withSettings from 'containers/SettingsProvider/withSettings';
import withDialog from 'containers/Dialog/withDialog';
import withKvApi from 'containers/KmVretersPage/withKvApi';
import Switch from 'components/Switch';
import Pagination from 'components/Pagination';
import { PAGESIZE } from 'utils/constants';
import formatDate from 'utils/formatDate';
import formatTime from 'utils/formatTime';
import listByDistances from './listByDistances';
import messages from '../messages';
import CandlestickChart from './CandlestickChart';
import getGraphData from './getGraphData';
import showLink from '../Races/showLink';

const SwitchWrapper = styled(Switch)`
  margin: 15px;
`;

const Wrapper = styled(Table)`
  @media (max-width: 479px) {
    font-size: smaller;
  }
  th,
  td {
    text-align: right;
  }
  .td-link {
    text-align: center;
  }
`;

function Distances(props) {
  const { data, units, intl } = props;
  const [selectedOnly, setSelectedOnly] = useState(true);
  const [offset, setOffset] = useState(0);

  const toggleSelected = useCallback(() => setSelectedOnly(s => !s), []);

  const handlePaging = useCallback(newOffset => setOffset(newOffset), []);

  const { list } = data.value;
  const tableList = listByDistances(list, units);
  const filteredList = tableList.filter(item => item.rank < PAGESIZE);
  const slicedList = selectedOnly
    ? filteredList
    : tableList.slice(offset, offset + PAGESIZE);

  const graphData = getGraphData(slicedList, units).map(item => ({
    ...item.data,
    tooltip: intl.formatMessage(
      item.quartiles ? messages.fullTooltip : messages.shortTooltip,
      item.values,
    ),
  }));

  return (
    <React.Fragment>
      {tableList.length > PAGESIZE && (
        <SwitchWrapper
          id="selectedOnly"
          value={selectedOnly}
          onChange={toggleSelected}
          size="small"
          inline
        >
          <FormattedMessage {...messages.selectedOnly} />
        </SwitchWrapper>
      )}
      <CandlestickChart data={graphData} />
      <Wrapper responsive hover>
        <thead>
          <tr>
            <th>
              <FormattedMessage {...messages.distance} />
            </th>
            <th>
              <FormattedMessage {...messages.number} />
            </th>
            <th>
              <FormattedMessage {...messages.avgTime} />
            </th>
            <th>
              <FormattedMessage {...messages.bestTime} />
            </th>
            <th>
              <FormattedMessage {...messages.date} />
            </th>
            <th className="td-link">
              <FormattedMessage {...messages.link} />
            </th>
          </tr>
        </thead>
        <tbody>
          {slicedList.map(item => (
            <tr key={item.distance}>
              <td className="td-distance">{`${item.distance} ${units}`}</td>
              <td className="td-number">{item.number}</td>
              <td className="td-time">{formatTime(item.avgTime)}</td>
              <td className="td-time">{formatTime(item.times[0])}</td>
              <td className="td-date" title={item.title}>
                {formatDate(item.date, 'PP')}
              </td>
              <td className="td-link">{showLink(item.link)}</td>
            </tr>
          ))}
        </tbody>
      </Wrapper>
      {!selectedOnly && (
        <Pagination
          value={offset}
          length={tableList.length}
          onChange={handlePaging}
        />
      )}
    </React.Fragment>
  );
}

Distances.propTypes = {
  data: PropTypes.object.isRequired,
  units: PropTypes.string.isRequired,
  dlgConfirm: PropTypes.func.isRequired,
  apiChange: PropTypes.func.isRequired,
  intl: intlShape,
};

export default compose(
  injectIntl,
  withDialog,
  withKvApi,
  withSettings(['units']),
)(Distances);
